import { useState, useCallback, useMemo } from 'react';
import { Alert } from '@piggytechng/component-library';
import { httpClient } from '../api/http';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { queryParamBuilder } from '../helpers/utils';
import { formatIpoInvestmentCard } from '../helpers/utils/investify';
import { InvestmentHistoryRow, IpoInvestment, IpoSubscription, InvestifyCategory } from '../types';

const HISTORY_PAGE_SIZE = 20;

// The history payload's field naming isn't fixed, so normalize the common variants.
const toHistoryRow = (raw: any, index: number): InvestmentHistoryRow => ({
  id: String(raw.INV2542d61cb1141b ?? raw.reference ?? raw.transactionId ?? index),
  date: Number(raw.aaisw_date ?? raw.created_at ?? raw.timestamp) || undefined,
  title: raw.aaisw_memo ?? raw.type ?? raw.action ?? raw.memo ?? raw.description ?? '-----',
  amount: Number(raw.aaisw_price ?? raw.value ?? raw.total) || 0,
  units: raw.aaisw_units ?? raw.unit ?? raw.quantity,
  reference: raw.aaisw_rand ?? raw.transactionId ?? raw.ref,
  status: raw.aaisw_status,
});

const useInvestify = (options?: {
  category?: InvestifyCategory;
  offerId?: string;
  /** Drives the investment-info, offers and history queries below. */
  investmentId?: string;
  offerListType?: 'buy' | 'buyback' | 'sell';
  /** History is only fetched while its tab is open. */
  enableHistory?: boolean;
}) => {
  const params = useParams();
  const userIdIn = params.id as string;

  // Reconcile is a mutation, so it keeps its own loading flag.
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [transactionsParams, setTransactionsParams] = useState({
    page: 1,
    limit: 20,
    category: options?.category || 'active',
  });

  const isIpoCategory = transactionsParams.category === 'ipo';

  const {
    data: InvestmentList,
    isLoading: isLoadingInvestmentList,
    isRefetching: isRefetchingInvestmentList,
  } = useQuery({
    queryKey: [`investify-investments-${userIdIn}`, transactionsParams],
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !isIpoCategory,
    queryFn: () =>
      httpClient
        .get(`admin/investify/investment-list/${userIdIn}` + queryParamBuilder(transactionsParams))
        .then((res) => res.data.data?.list),
  });

  const {
    data: IpoInvestmentList,
    isLoading: isLoadingIpoInvestmentList,
    isRefetching: isRefetchingIpoInvestmentList,
  } = useQuery({
    queryKey: [`investify-ipo-investments-${userIdIn}`, transactionsParams],
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: isIpoCategory,
    queryFn: () =>
      httpClient
        .get(`admin/investify/ipo-investments/${userIdIn}` + queryParamBuilder(transactionsParams))
        .then((res) => {
          const payload = res.data?.data;
          const subscriptions: IpoInvestment[] = payload?.data?.subscriptions ?? [];
          return subscriptions.map(formatIpoInvestmentCard);
        }),
  });

  const { data: IpoSubscriptions = [], isLoading: isLoadingIpoSubscriptions } = useQuery({
    queryKey: [`investify-ipo-subscriptions-${userIdIn}`, options?.offerId],
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: Boolean(userIdIn && options?.offerId),
    queryFn: () =>
      httpClient.get(`admin/investify/ipo-subscriptions/${userIdIn}`).then((res) => {
        const subscriptions: IpoSubscription[] = res.data?.data?.subscriptions ?? [];
        return subscriptions.filter((sub) => sub.offer_id === options?.offerId);
      }),
  });

  const { data: InvestmentListQ, isLoading: isLoadingInvestmentListQ } = useQuery({
    queryKey: [`investify-investment-list-summary-${userIdIn}`],
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: () => httpClient.get(`admin/investify/investment-list/${userIdIn}`).then((res) => res.data.data),
  });

  const { data: InvestmentData, isLoading: isLoadingInvestmentData } = useQuery({
    queryKey: ['investify-investment-info', userIdIn, options?.investmentId],
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: Boolean(userIdIn && options?.investmentId),
    queryFn: () =>
      httpClient
        .get(`admin/investify/investment-info/${userIdIn}/${options?.investmentId}`)
        .then((res) => res.data.data),
  });

  const { data: InvestmentOffers = [], isLoading: isLoadingOffers } = useQuery({
    queryKey: ['investify-investment-offers', options?.investmentId, options?.offerListType],
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: Boolean(options?.investmentId && options?.offerListType),
    queryFn: () =>
      httpClient
        .get(`admin/investify/investment-offers/${options?.investmentId}/?listType=${options?.offerListType}`)
        .then((res) => res.data.data?.offersList ?? []),
  });

  const {
    data: historyPages,
    isLoading: isLoadingHistory,
    isFetchingNextPage: isLoadingMoreHistory,
    hasNextPage: historyHasMore,
    fetchNextPage: loadMoreHistory,
  } = useInfiniteQuery({
    queryKey: ['investify-investment-history', userIdIn, options?.investmentId],
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: Boolean(userIdIn && options?.investmentId && options?.enableHistory),
    initialPageParam: 1,
    getNextPageParam: (lastPage: InvestmentHistoryRow[], allPages) =>
      lastPage.length === HISTORY_PAGE_SIZE ? allPages.length + 1 : undefined,
    queryFn: ({ pageParam }) =>
      httpClient
        .get(
          `admin/investify/investment-history/${userIdIn}/${options?.investmentId}/${pageParam}/${HISTORY_PAGE_SIZE}`,
        )
        .then((res) => {
          const data = res.data?.data;
          const list: any[] =
            data?.history ?? data?.list ?? data?.results ?? data?.transactions ?? (Array.isArray(data) ? data : []);
          return list.map(toHistoryRow);
        }),
  });

  const InvestmentHistory = useMemo(() => historyPages?.pages.flat() ?? [], [historyPages]);

  const reconcileInvestment = useCallback(
    async (userId: string, investmentId: string, payload: any, callback?: () => void) => {
      try {
        setIsLoading(true);
        const url = 'admin/investify/reconcile-investment';
        const response = await httpClient.post(url, {
          userId,
          investmentId,
          ...payload,
        });
        if (response.data.status) {
          Alert.success('Investment Reconciled successfully');
          if (callback) callback();
        }
      } catch (error: any) {
        console.log(error);
        Alert.error(error?.response?.data?.message || 'Error reconciling investment');
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return {
    isLoading,
    isLoadingOffers,
    isLoadingInvestmentData,
    InvestmentList: isIpoCategory ? IpoInvestmentList : InvestmentList,
    InvestmentData,
    InvestmentOffers,
    IpoSubscriptions,
    isLoadingIpoSubscriptions,
    setTransactionsParams,
    transactionsParams,
    isLoadingInvestmentList: isIpoCategory ? isLoadingIpoInvestmentList : isLoadingInvestmentList,
    isRefetchingInvestmentList: isIpoCategory ? isRefetchingIpoInvestmentList : isRefetchingInvestmentList,
    reconcileInvestment,
    InvestmentListQ,
    isLoadingInvestmentListQ,
    InvestmentHistory,
    isLoadingHistory,
    isLoadingMoreHistory,
    historyHasMore,
    loadMoreHistory,
  };
};

export default useInvestify;
